import _ from 'lodash';
import React, { Dispatch, FunctionComponent, useEffect, useReducer, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Button, Modal } from 'react-bootstrap';
import Label from '../Label';

/* Though QM will ask us for post op fields in the form of
   MULTISERVICE:AUTH or IBSEQ:AUTH, each with their own regex rules,
   the response back to QM only expects AUTH:VALUE, etc.
*/

interface IProps {
  container: Object;
  hide: () => void;
  onPostOpProcess: (fleetBillingPostOpFields: string[]) => void;
  postOpFieldsRequested: string[];
  show: boolean;
}

type ValueType = 'AUTH' | 'OVER' | 'UNIT' | 'VIN';
type ValuesState = {
  [k in ValueType]?: {
    error: string | null;
    value: string | null;
  };
};

const FleetBillingPostOpModal: FunctionComponent<IProps> = ({
  container,
  hide,
  onPostOpProcess,
  postOpFieldsRequested,
  show,
}) => {
  const [postOpFields, setPostOpFields] = useReducer(
    (prevState: ValuesState, newState: ValuesState) => ({ ...prevState, ...newState }),
    {}, // initialArg
    (initialState: ValuesState) => {
      postOpFieldsRequested.forEach(field => {
        ['AUTH', 'OVER', 'UNIT', 'VIN'].forEach(fieldType => {
          if (~field.indexOf(fieldType)) {
            initialState[fieldType] = {
              error: null,
              value: null,
            };
          }
        });
      });
      return initialState;
    }
  ) as [ValuesState, Dispatch<ValuesState>];

  // Focus to first input displayed on modal render
  useEffect(() => {
    setTimeout(() => {
      const el = document.querySelector('.sk-fbPostOpItem:first-child input');
      if (el) {
        (el as HTMLInputElement).focus();
      }
    }, 0);
  }, []);

  const processButtonRef = useRef<HTMLButtonElement>(null);

  /* In theory this hook can set multiple fields at once:
     setPostOpFieldValues({
       'AUTH': { value: '1234', error: 'bad regex' },
       ...
     });
  */
  const renderPostOpField = (field: string, index: number) => {
    const render = (label: string, type: ValueType, errorFunc: (value: string | null) => string | null) => (
      <>
        <Label>{label}</Label>
        <input
          id={`sk-postOpField-${index}`}
          onBlur={evt => {
            const value = evt.target.value.trim() || null;
            setPostOpFields({
              [type]: {
                error: errorFunc(value),
                value,
              },
            });

            // see if there's another input following this to focus to
            const el = document.getElementById(`sk-postOpField-${index + 1}`);
            // if not, skip the cancel button for process speed / accidental modal closure
            if (!el) {
              setTimeout(() => {
                const processButtonEl = ReactDOM.findDOMNode(processButtonRef.current!) as HTMLButtonElement;
                if (!processButtonEl.disabled) {
                  processButtonEl.focus();
                } else {
                  (document.activeElement as any).blur();
                }
              }, 100);
            }
          }}
          onKeyPress={evt => {
            if (evt.key === 'Enter') {
              // see if there's another input following this to focus to
              const el = document.getElementById(`sk-postOpField-${index + 1}`);
              if (el) {
                el.focus();
              } else {
                // Final input in the list here, blur it then focus to process if possible (fields all validated)
                evt.currentTarget.blur();
                setTimeout(() => (ReactDOM.findDOMNode(processButtonRef.current!) as HTMLButtonElement).focus(), 100);
              }
            }
          }}
          type="text"
        />
        {postOpFields[type]!.error && <div className="sk-fbPostOpError">{postOpFields[type]!.error}</div>}
      </>
    );

    switch (field) {
      case 'IBSREQ:AUTH':
        return render('APPROVAL #', 'AUTH', value =>
          /^[A-Za-z]{2}\d{4}$/.test(value || '') ? null : 'Must be 2A4N (2 letters followed by 4 numbers).'
        );

      case 'MULTISERVICE:AUTH':
        return render('APPROVAL #', 'AUTH', value =>
          /^\d{4,6}$/.test(value || '') ? null : 'Must be 4-6 numeric characters.'
        );

      case 'MULTISERVICE:UNIT':
        return render('UNIT #', 'UNIT', value => (value ? null : 'Please enter a value.'));

      case 'MULTISERVICE:VIN':
        return render('VIN #', 'VIN', value => (value ? null : 'Please enter a value.'));

      case 'VOLVOINQ:AUTH':
        return render('APPROVAL #', 'AUTH', value => (value ? null : 'Please enter a value.'));

      case 'VOLVOINQ:OVER':
        return render('DAYS APPROVAL GOOD FOR', 'OVER', value =>
          /^0$|^[1-9][0-9]?$|^100$/.test(value || '') ? null : 'Must be between 0-100 days.'
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      backdrop="static"
      container={container as any}
      dialogClassName="sk-fleetBillingPostOpModal"
      onHide={hide as any}
      show={show}
    >
      <Modal.Header closeButton={true}>
        <Modal.Title>Fleet Billing Required Fields</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {postOpFieldsRequested.map((field, index) => (
          <div className="sk-fbPostOpItem" key={index}>
            {renderPostOpField(field, index)}
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hide} variant="secondary">
          Cancel
        </Button>
        <Button
          variant="success"
          disabled={_.some(Object.values(postOpFields), o => o!.error !== null || o!.value === null)}
          onClick={() => {
            const modalResult = [];
            for (const field of Object.keys(postOpFields)) {
              modalResult.push(`${field}:${postOpFields[field].value}`);
            }
            onPostOpProcess(modalResult);
          }}
          ref={processButtonRef}
        >
          Process
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FleetBillingPostOpModal;
